import * as React from 'react';

export type FilterValue = string | string[] | undefined;

export interface FilterOption {
  value: string;
  label: string;
  icon?: React.ReactNode;
}

export type FilterType = 'select' | 'multiselect' | 'text';

export interface FilterDef {
  /** Stable identity, e.g. 'agent'. */
  id: string;
  /** Human label shown in the menu, trigger, and chip. */
  label: string;
  /** Optional lucide icon shown alongside the label. */
  icon?: React.ReactNode;
  type: FilterType;

  /** Primary URL param this filter reads/writes (default serialization). */
  urlParam: string;

  /** Options for select / multiselect filters. */
  options?: FilterOption[];

  /** Render as an always-visible quick-filter button in the toolbar. */
  pinned?: boolean;

  /** Show a search box inside the dropdown (for long option lists). */
  searchable?: boolean;

  /**
   * Value treated as "empty"/cleared for select filters (deletes the param).
   * Defaults to 'all'.
   */
  emptyValue?: string;

  /** Placeholder for text inputs. */
  placeholder?: string;

  /**
   * Custom URL serialization. Receives the UI value, returns the params to
   * write (a `null` value deletes that param). Use for filters whose URL
   * representation differs from the UI value (e.g. status active -> is_active=true).
   * When provided, `fromParam` should be provided too.
   */
  toParam?: (value: FilterValue) => Record<string, string | null>;
  /** Custom URL deserialization, paired with `toParam`. */
  fromParam?: (params: URLSearchParams) => FilterValue;
}

export const DEFAULT_EMPTY_VALUE = 'all';

export type SortOrder = 'asc' | 'desc';

export interface SortDef {
  /** Matches the table column id, used for the header direction indicator. */
  id: string;
  label: string;
  /** Value sent as `sort_by` to the backend. */
  field: string;
  /** Direction applied when this field is first selected. Defaults to 'desc'. */
  defaultOrder?: SortOrder;
}
